import { useMemo, useCallback } from 'react';
import {
    BOOSTS_TAB_ROUTE_SEGMENT,
    DAMAGE_TAB_ROUTE_SEGMENT,
    localGenPath,
    routeSegmentForTab,
} from '../utils/buildRoutes';

function tabFromRoute(dataTab) {
    if (dataTab === DAMAGE_TAB_ROUTE_SEGMENT || dataTab === 'spells') return 'spells';
    if (dataTab === BOOSTS_TAB_ROUTE_SEGMENT) return 'boosts';
    return 'stats';
}

/**
 * Data tab (stats/spells/boosts) selection + URL sync for the Generator page.
 *
 * `activeDataTab` is derived from the `:dataTab` route param rather than held
 * as its own state — the route is the source of truth. Changing tabs
 * navigates to the canonical build path with the tab's own route segment, or
 * without one for stats. See docs/generator-slicing.md cluster C.
 *
 * Does NOT own `dataTabsElement` (the `<Tabs>` JSX) — that stays in
 * Generator.js because it also needs cluster B's badge counts/tooltips.
 */
export function useDataTabRouting({ dataTab, navigate, getActiveBuildPath, lang }) {
    const activeDataTab = useMemo(() => tabFromRoute(dataTab), [dataTab]); // 'stats' | 'spells' | 'boosts'

    const handleDataTabChange = useCallback((arg1, arg2) => {
        // Support both signatures: onChange(value) and onChange(event, value)
        const rawNextTab = typeof arg1 === 'string' ? arg1 : arg2;
        const nextTab = tabFromRoute(rawNextTab);
        if (nextTab === activeDataTab) return;
        const segment = routeSegmentForTab(nextTab);
        navigate(getActiveBuildPath(lang, segment) || localGenPath(lang, segment));
    }, [activeDataTab, getActiveBuildPath, lang, navigate]);

    return { activeDataTab, handleDataTabChange };
}
